import { Consent } from '../../dataTypes/type-consent';
import { GetConsentsService } from '../consents-services/get-consents.service';


export class DataService {
    public static consentStorageKey = 'ucConsents';
    public static settingStorageKey = 'ucSettings';
    public static localStorageKey = 'usercentrics';
    public static windowVariableKey = 'usercentrics';

    private static validGlobalVariables = ['settings', 'categories', 'consents', 'consentTemplates', 'controllerId'];

    public static settings;
    public static consentsHash: String;
    public static categories: Consent.Category[] = [];
    public static consents: Consent.Consent[] = [];
    public static consentTemplates: Consent.ConsentTemplates;
    public static controllerId;

    static storeGlobally(key, value) {
        DataService.storeToGlobalVariable(key, value);
        DataService.storeToWindowVariable(key,value);
        DataService.storeConsentsToLocalStorage(key, value);
        const keysForDeprecatedSync = ['consents', 'consentTemplates'];
        if (keysForDeprecatedSync.indexOf(key) !== -1 && DataService.consentTemplates && DataService.consents && DataService.settings) {
            DataService.updateDeprecatedLocalstorage();
        }
    }

    /* eslint-disable max-lines-per-function */
    static updateDeprecatedConsents(consents: Consent.Consent[], consentTemplates: Consent.ConsentTemplates, settings) {
        return consents.map((e: Consent.Consent) => {
            // This might happen as a result of a race condition when loading templates
            if (!consentTemplates[e.templateId] || !consentTemplates[e.templateId][e.version]) {
                return e;
            }
            return {
                adminSettingsId: consentTemplates[e.templateId][e.version].adminSettingsId,
                categorySlug: e.categorySlug,
                consentId: GetConsentsService.getConsentId(e),
                consentStatus: e.unsavedConsentStatus,
                controllerId: e.controllerId,
                cookieNames: consentTemplates[e.templateId][e.version].cookieNames,
                cookiePolicyURL: consentTemplates[e.templateId][e.version].cookiePolicyURL,
                createdAt: e.history.length ? e.history[e.history.length - 1].updatedAt : '',
                dataAttributes: consentTemplates[e.templateId][e.version].dataAttributes,
                dataAttributesHasData: consentTemplates[e.templateId][e.version].dataAttributes.length ? true : false,
                dataCollectedDescription: consentTemplates[e.templateId][e.version].dataCollectedDescription,
                dataProcessors: consentTemplates[e.templateId][e.version].dataProcessors,
                dataProtectionOfficer: consentTemplates[e.templateId][e.version].dataProtectionOfficer,
                dataPurposes: consentTemplates[e.templateId][e.version].dataPurposes,
                dataPurposesDescription: consentTemplates[e.templateId][e.version].dataPurposesDescription,
                dataPurposesHasData: consentTemplates[e.templateId][e.version].dataPurposes.length ? true : false,
                dataPurposesList: consentTemplates[e.templateId][e.version].dataPurposesList,
                dataRecipientsDescription: consentTemplates[e.templateId][e.version].dataRecipientsDescription,
                dataRecipientsList: consentTemplates[e.templateId][e.version].dataRecipientsList,
                dataScopes: settings.dataScopes,
                dataScopesHasData: settings.dataScopes.length ? true : false,
                defaultCategoryLabel: consentTemplates[e.templateId][e.version].defaultCategoryLabel,
                defaultCategorySlug: consentTemplates[e.templateId][e.version].defaultCategorySlug,
                defaultConsentStatus: consentTemplates[e.templateId][e.version].defaultConsentStatus,
                defaultDescription: consentTemplates[e.templateId][e.version].description,
                description: e.description,
                domAttributes: consentTemplates[e.templateId][e.version].domAttributes,
                domElements: consentTemplates[e.templateId][e.version].domElements,
                domKeys: consentTemplates[e.templateId][e.version].domKeys,
                history: e.history,
                iabId: consentTemplates[e.templateId][e.version].iabId,
                id: consentTemplates[e.templateId][e.version].templateId,
                index: null, // removed in new DataStructure
                isDeactivated: consentTemplates[e.templateId][e.version].isDeactivated,
                isEssential: GetConsentsService.isConsentEssential(e),
                isHidden: consentTemplates[e.templateId][e.version].isHidden,
                isLatest: false, // removed in new DataStructure
                isPredefined: false, // removed in new DataStructure
                isShared: consentTemplates[e.templateId][e.version].isShared,
                isVisible: !consentTemplates[e.templateId][e.version].isHidden,
                language: consentTemplates[e.templateId][e.version].language,
                languagesAvailable: [consentTemplates[e.templateId][e.version].language], // removed in new DataStructure
                legalBasisDescription: consentTemplates[e.templateId][e.version].legalBasisDescription,
                legalBasisList: consentTemplates[e.templateId][e.version].legalBasisList,
                legalGround: consentTemplates[e.templateId][e.version].legalGround,
                linkToDpa: consentTemplates[e.templateId][e.version].linkToDpa,
                locationOfProcessing: consentTemplates[e.templateId][e.version].locationOfProcessing,
                optOutDescription: consentTemplates[e.templateId][e.version].optOutDescription,
                optOutUrl: consentTemplates[e.templateId][e.version].optOutUrl,
                policyOfProcessorUrl: consentTemplates[e.templateId][e.version].policyOfProcessorUrl,
                processorId: e.processorId,
                recordsOfProcessingActivities: consentTemplates[e.templateId][e.version].recordsOfProcessingActivities,
                retentionPeriod: consentTemplates[e.templateId][e.version].retentionPeriod,
                retentionPeriodDescription: consentTemplates[e.templateId][e.version].retentionPeriodDescription,
                retentionPeriodList: consentTemplates[e.templateId][e.version].retentionPeriodList,
                selectedUndefined: false,
                settingsId: settings.settingsId,
                settingsVersion: e.history.length ? e.history[0].settingsVersion : settings.settingsVersion,
                shareCustomConsent: consentTemplates[e.templateId][e.version].isShared,
                shortid: e.templateId,
                showHistory: false,
                subConsents: e.subConsents,
                subConsentsActive: false,
                subConsentsHasData: false,
                tagLoggerMatchingId: consentTemplates[e.templateId][e.version].tagLoggerMatchingId,
                technologyUsed: consentTemplates[e.templateId][e.version].technologyUsed,
                templateId: e.templateId,
                thirdCountryTransfer: consentTemplates[e.templateId][e.version].thirdCountryTransfer,
                type: 'predefined',
                updatedAt: e.history.length ? e.history[0].updatedAt : '',
                updatedVersion: consentTemplates[e.templateId][e.version].version,
                version: consentTemplates[e.templateId][e.version].version
            };
        });
    }
    /* eslint-enable max-lines-per-function */

    static updateDeprecatedLocalstorage() {
        const deprecatedUsercentrics = DataService.getLocalStorage();
        if (DataService.consents) {
            const categories = DataService.categories.map((e) => {
                return {
                    active: false,
                    categorySlug: e.categorySlug,
                    description: e.description,
                    consents: DataService.updateDeprecatedConsents(e.consents, DataService.consentTemplates, DataService.settings),
                    haveConsents: e.consents.length,
                    id: e.categorySlug,
                    isEssential: e.isEssential,
                    isVisible: !e.isHidden,
                    label: e.label,
                    name: e.label,
                    selected: e.status,
                    selectedUndefined: e.isIntermediateState
                };
            });
            const consents = DataService.updateDeprecatedConsents(DataService.consents, DataService.consentTemplates, DataService.settings);
            deprecatedUsercentrics.consents = consents;
            deprecatedUsercentrics.categories = categories;
            localStorage.setItem(DataService.localStorageKey, JSON.stringify(deprecatedUsercentrics));
        }
    }

    /**
     * Writes this settings data (setting & consents) into the overall local storage
     * that holds information of all settings.
     * @param id 
     */
    static updateSettingsStorage(id) {
        let usercentrics: string | null = localStorage.getItem(DataService.settingStorageKey);
        const ucSettings = usercentrics ? JSON.parse(usercentrics) : {};
        ucSettings[id] = {
            usercentrics: DataService.getLocalStorage(),
            ucConsents: DataService.getConsentsFromLocalStorage()
        };
        localStorage.setItem(DataService.settingStorageKey, JSON.stringify(ucSettings));
    }

    static storeConsentsToLocalStorage(key, value) {
        let usercentrics: any = localStorage.getItem(DataService.consentStorageKey);
        usercentrics = usercentrics ? JSON.parse(usercentrics) : {};
        usercentrics[key] = value;
        localStorage.setItem(DataService.consentStorageKey, JSON.stringify(usercentrics));
    }

    static getConsentsFromLocalStorage(key?) {
        let usercentrics: any = localStorage.getItem(DataService.consentStorageKey);
        usercentrics = usercentrics ? JSON.parse(usercentrics) : {};
        return key? usercentrics[key] : usercentrics;
    }

    static storeToLocalStorage(key, value) {
        let usercentrics: any = localStorage.getItem(DataService.localStorageKey);
        usercentrics = usercentrics ? JSON.parse(usercentrics) : {};
        usercentrics[key] = value;
        localStorage.setItem(DataService.localStorageKey, JSON.stringify(usercentrics));

        const keysForDeprecatedSync = ['settings'];
        if (keysForDeprecatedSync.indexOf(key) !== -1 && DataService.consentTemplates && DataService.consents && DataService.settings) {
            DataService.updateDeprecatedLocalstorage();
        }
    }

    static removeLocalStorage() {
        window.localStorage.removeItem(this.localStorageKey);
    }

    static getLocalStorage(key?) {
        let usercentrics: any = localStorage.getItem(DataService.localStorageKey);
        usercentrics = usercentrics ? JSON.parse(usercentrics) : {};
        return key? usercentrics[key] : usercentrics;
    }

    static storeToWindowVariable(key, value) {
        const usercentrics: any = window[DataService.windowVariableKey] || {};
        if (value) {
            usercentrics[key] = JSON.parse(JSON.stringify(value));
        }
    }

    static storeToGlobalVariable(key, value) {
        if (DataService.validGlobalVariables.indexOf(key) !== -1) {
            DataService[key] = value;
        }
    }
}
